// Single source of truth for plan definitions. Keep in sync with backend
// PLAN_PRICING / PLAN_REVIEW_LIMITS in backend/main.py.
//
// Pricing v2 (monthly base + metered overage):
//   standard = "Agent" — $25/mo, 3 reports included, then $10/report
//   pro      = "Team"  — $149/mo, 20 reports included, then $6/report
//   brokerage          — contact sales (no self-serve plan)
// There is NO hard cap: reports beyond the included allotment bill as overage.

export type PlanType = 'standard' | 'pro' | 'brokerage';
export type PaidPlanKey = 'standard' | 'pro';

export interface PlanInfo {
  key: PaidPlanKey;
  name: string;        // display name
  price: string;       // e.g. "$25/month"
  priceMonthly: number;
  included: number;    // reports included in the base price
  overage: number;     // $ per report beyond the included allotment
}

export const PLANS: Record<PaidPlanKey, PlanInfo> = {
  standard: { key: 'standard', name: 'Agent', price: '$25/month', priceMonthly: 25, included: 3, overage: 10 },
  pro:      { key: 'pro',      name: 'Team',  price: '$149/month', priceMonthly: 149, included: 20, overage: 6 },
};

// Included reports per month by plan (base allotment, not a hard cap).
export const PLAN_REVIEW_LIMITS: Record<PaidPlanKey, number> = {
  standard: PLANS.standard.included,
  pro: PLANS.pro.included,
};

// A user can generate iff they hold an active paid plan.
export const hasActivePlan = (plan: string | undefined): boolean =>
  plan === 'standard' || plan === 'pro';

export const getPlanDisplayName = (plan: string | undefined): string => {
  if (plan === 'standard') return 'Agent';
  if (plan === 'pro') return 'Team';
  if (plan === 'brokerage') return 'Brokerage';
  return 'No plan';
};

// Reports included in the plan's base price (0 if no active plan).
export const getReviewLimit = (plan: string | undefined): number => {
  if (plan === 'standard') return PLAN_REVIEW_LIMITS.standard;
  if (plan === 'pro') return PLAN_REVIEW_LIMITS.pro;
  return 0;
};

// Included reports still remaining before overage rates kick in.
export const getReviewsRemaining = (plan: string | undefined, used: number): number => {
  return Math.max(0, getReviewLimit(plan) - (used || 0));
};

// Overage $/report for the plan (0 if no active plan).
export const getOverageRate = (plan: string | undefined): number => {
  if (plan === 'standard') return PLANS.standard.overage;
  if (plan === 'pro') return PLANS.pro.overage;
  return 0;
};

// Number of reports billed as overage this cycle (used beyond included).
export const getOverageCount = (plan: string | undefined, used: number): number => {
  return Math.max(0, (used || 0) - getReviewLimit(plan));
};
